import Button from '@zeptonow/zui/Button';
import Modal from '@zeptonow/zui/Modal';
import Typography from '@zeptonow/zui/Typography';
import { generateImage } from '../../utils/generateImage';
import {
  ConfirmationModalProps,
  DiscardChangesModalProps,
  SaveConfigurationModalProps,
} from './types';

export const SaveConfigurationModal = ({
  showSaveConfigurationModal,
  onCancelClick,
  onDiscardChangesClick,
  onSaveClick,
}: SaveConfigurationModalProps) => {
  return (
    <Modal
      open={showSaveConfigurationModal}
      className="!max-w-[28rem] !mx-auto"
    >
      <Modal.Title>
        <div className="flex justify-between items-center">
          {generateImage({
            name: 'info-circle-with-background',
            alt: 'info-circle-with-background icon',
            className: 'w-14 h-14',
          })}
          <Button
            id="hide-save-config-modal"
            onClick={onCancelClick}
            variant="text"
            className="!min-w-fit"
          >
            {generateImage({
              name: 'cross',
              alt: 'cross icon',
              className: 'h-4 w-4',
            })}
          </Button>
        </div>
      </Modal.Title>
      <Modal.Content>
        <Typography variant="h5" className="!text-gray-900 !font-medium !mb-2">
          Configuration not saved
        </Typography>
        <Typography variant="body1">
          You haven’t saved your permissions for the role. Please save them
          before moving on.
        </Typography>
      </Modal.Content>
      <Modal.Actions>
        <div className="flex justify-between items-center w-full">
          <Button
            onClick={onDiscardChangesClick}
            variant="text"
            className="!flex-1 !text-red-700"
          >
            Discard Changes
          </Button>
          <Button
            onClick={onSaveClick}
            variant="contained"
            className="!flex-1 !ml-1"
          >
            Save
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};

//TODO: use common modal component
export const DiscardChangesModal = ({
  showDiscardChangesModal,
  onCancelDiscardChanges,
  onConfirmDiscardChanges,
}: DiscardChangesModalProps) => {
  return (
    <Modal open={showDiscardChangesModal} className="!max-w-[28rem] !mx-auto">
      <Modal.Title>
        <div className="flex justify-between items-center">
          {generateImage({
            name: 'error-circle-with-background',
            alt: 'error circle icon',
            className: 'h-14 w-14',
          })}

          <Button
            onClick={onCancelDiscardChanges}
            variant="text"
            className="!min-w-fit"
          >
            {generateImage({
              name: 'cross',
              alt: 'cross icon',
              className: 'h-4 w-4',
            })}
          </Button>
        </div>
      </Modal.Title>
      <Modal.Content>
        <Typography variant="h5" className="!text-gray-900 !font-medium !mb-2">
          Do you want to discard changes?
        </Typography>
        <Typography variant="body1">
          If you discard these changes all your progress will be lost.
        </Typography>
      </Modal.Content>
      <Modal.Actions>
        <div className="flex justify-between items-center w-full">
          <Button
            onClick={onConfirmDiscardChanges}
            variant="contained"
            className="!flex-1 !m-0 !bg-red-600"
          >
            Discard Changes
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};

export const ConfirmationModal = ({
  title,
  description,
  icon,
  open,
  handleClose,
  handleAction,
  actionButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  actionButtonColor = 'primary',
  modalClassName = '',
  variant = 'info',
  actionButtonDisabled = false,
}: ConfirmationModalProps) => {
  const successVariantBG =
    variant === 'success' ? 'bg-green-150' : 'bg-primary-150';
  const successVariantIcon =
    variant === 'success' ? 'check-circle' : 'info-icon';
  return (
    <Modal
      open={open}
      onClose={handleClose}
      maxWidth="md"
      className={modalClassName ?? ''}
    >
      <Modal.Title>
        <Button
          id="hide-save-config-modal"
          onClick={handleClose}
          variant="text"
          className="!absolute !top-3 !right-0"
        >
          {generateImage({
            name: 'cross',
            alt: 'cross icon',
            className: 'h-3 w-3',
          })}
        </Button>
        <span className="flex flex-col items-center">
          {icon ?? (
            <div
              className={`rounded-full flex items-center justify-center ${
                variant === 'error' ? 'bg-red-150' : successVariantBG
              } w-12 h-12 p-3`}
            >
              {generateImage({
                name: variant === 'error' ? 'x-cross' : successVariantIcon,
                alt: `${variant} icon`,
                className: 'h-8 w-8',
              })}
            </div>
          )}
        </span>
        <Typography
          variant="h5"
          className="!text-gray-900 !mt-6 !font-medium !mb-0 text-center"
        >
          {title}
        </Typography>
      </Modal.Title>
      {description ? (
        <Modal.Content>
          <Typography variant="body1" className="!text-gray-500 text-center">
            {description}
          </Typography>
        </Modal.Content>
      ) : null}
      <Modal.Actions>
        <div className="flex gap-2 justify-between items-center w-full mt-8">
          <Button
            onClick={handleClose}
            variant="outlined"
            size="medium"
            className="flex-1 mr-2"
          >
            {cancelButtonText ?? 'Cancel'}
          </Button>
          <Button
            onClick={handleAction}
            variant="contained"
            size="medium"
            color={actionButtonColor ?? 'primary'}
            className="flex-1"
            disabled={actionButtonDisabled}
          >
            {actionButtonText ?? 'Confirm'}
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
